import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { PaymentProviderDialog } from '../components/PaymentProviderDialog';
import { HandHeart, CheckCircle2, ChevronLeft, Loader } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PhysioPackagesPage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isRTL = language === 'ar';

  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payOpen, setPayOpen] = useState(false);
  const [payPkg, setPayPkg] = useState(null);

  useEffect(() => {
    axios.get(`${API}/physio/packages`)
      .then((r) => setPackages(r.data))
      .catch((err) => console.warn('packages load failed', err))
      .finally(() => setLoading(false));
  }, []);

  const titleFor = (pkg) =>
    language === 'fr' ? pkg.title_fr
      : language === 'en' ? pkg.title_en
      : pkg.title_ar;

  const handleSelect = async (pkg) => {
    if (!user) {
      navigate(`/login?next=/physio`);
      return;
    }
    // If the patient already has an active purchase for this package, skip
    // payment and go straight to the info form.
    try {
      const r = await axios.get(`${API}/physio/my-purchase`, { withCredentials: true });
      if (r.data?.active && r.data.package_id === pkg.id && r.data.sessions_remaining > 0) {
        navigate(`/physio/book/${pkg.id}`);
        return;
      }
    } catch { /* fall through to payment */ }
    setPayPkg(pkg);
    setPayOpen(true);
  };

  return (
    <div className={`min-h-screen bg-gradient-to-b from-blue-50 to-white ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'} data-testid="physio-packages-page">
      <header className="bg-gradient-to-r from-[#1565c0] to-[#0d47a1] text-white px-4 py-6 shadow-md">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <Link to="/" className="hover:bg-white/15 rounded-full p-2 transition" data-testid="back-home-link">
            <ChevronLeft size={22} className={isRTL ? 'rotate-180' : ''} />
          </Link>
          <HandHeart size={26} />
          <h1 className="text-xl md:text-2xl font-extrabold tracking-tight">
            {t('🎯 باقات العلاج الفيزيائي', 'Physiotherapy Packages', 'Forfaits de kinésithérapie')}
          </h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center py-16"><Loader className="animate-spin text-[#1565c0]" size={28} /></div>
        ) : (
          <div className="space-y-4" data-testid="physio-packages-list">
            {packages.map((pkg) => (
              <Card
                key={pkg.id}
                className={`relative p-5 md:p-6 border-l-4 ${isRTL ? 'border-l-0 border-r-4' : ''} border-[#1e88e5] hover:shadow-lg transition-shadow overflow-hidden`}
                data-testid={`physio-package-${pkg.id}`}
              >
                {pkg.offer_price_usd && pkg.offer_price_usd < pkg.price_usd && (
                  <span
                    className={`absolute top-3 ${isRTL ? 'left-3' : 'right-3'} bg-gradient-to-r from-[#ef5350] to-[#e53935] text-white text-[11px] font-extrabold px-2.5 py-1 rounded-full shadow-sm`}
                    data-testid={`offer-badge-${pkg.id}`}
                  >
                    {t(`خصم ${Math.round((1 - pkg.offer_price_usd / pkg.price_usd) * 100)}%`,
                       `${Math.round((1 - pkg.offer_price_usd / pkg.price_usd) * 100)}% OFF`,
                       `-${Math.round((1 - pkg.offer_price_usd / pkg.price_usd) * 100)}%`)}
                  </span>
                )}
                <div className={`flex items-start justify-between gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className="flex-1">
                    <div className={`flex items-center gap-2 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <CheckCircle2 className="text-[#43a047]" size={22} />
                      <h3 className="text-lg md:text-xl font-extrabold text-[#0d2c54]">
                        {titleFor(pkg)}
                      </h3>
                    </div>
                    <p className="text-gray-600 text-sm md:text-base flex items-center flex-wrap gap-2">
                      <span>
                        <span className="font-bold text-[#0d2c54]">{pkg.sessions}</span>{' '}
                        {t('جلسات', 'sessions', 'séances')}
                      </span>
                      <span className="text-gray-300">|</span>
                      {pkg.offer_price_usd && pkg.offer_price_usd < pkg.price_usd ? (
                        <span className="flex items-baseline gap-2" data-testid={`price-${pkg.id}`}>
                          <span className="text-gray-400 line-through text-sm font-semibold" data-testid={`price-original-${pkg.id}`}>
                            ${pkg.price_usd}
                          </span>
                          <span className="font-extrabold text-[#e53935] text-lg" data-testid={`price-offer-${pkg.id}`}>
                            ${pkg.offer_price_usd}
                          </span>
                        </span>
                      ) : (
                        <span className="font-extrabold text-[#1565c0]" data-testid={`price-${pkg.id}`}>
                          ${pkg.price_usd}
                        </span>
                      )}
                    </p>
                  </div>
                  <Button
                    onClick={() => handleSelect(pkg)}
                    className="bg-[#1565c0] hover:bg-[#0d47a1] h-10 rounded-full px-5 font-bold whitespace-nowrap shadow-sm"
                    data-testid={`select-package-${pkg.id}`}
                  >
                    {t('احجز', 'Book', 'Réserver')}
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        <p className="text-center text-xs text-gray-500 mt-8 leading-relaxed">
          {t('بعد الدفع، ستملأ استمارة المعلومات وسيتواصل معك فريقنا لتنسيق الموعد.',
            'After payment, fill in the info form and our team will contact you to arrange the session.',
            "Après paiement, remplissez le formulaire et notre équipe vous contactera pour organiser la séance.")}
        </p>
      </main>

      {payPkg && (
        <PaymentProviderDialog
          open={payOpen}
          onOpenChange={setPayOpen}
          packageId={payPkg.id}
          packageKind="physio"
          packageNameEn={payPkg.title_en}
          packageNameAr={payPkg.title_ar}
          amount={payPkg.offer_price_usd || payPkg.price_usd}
        />
      )}
    </div>
  );
};

export default PhysioPackagesPage;
